import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	InputAdornment,
	MenuItem,
	Paper,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { Email } from "@mui/icons-material";
import moment from "moment";
import { Client } from "../../../../client_api/model/client";
import { useProfile } from "../../../context/ProfileContext";
import { AutoCompleteClient, AutoCompletePermission, ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../shared/forms";
import { VMaskTextField } from "../../../shared/forms/VMaskTextField";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { PermissionServices } from "../../../shared/services/api/permissions/PermissionService";
import { ResetPasswordService } from "../../../shared/services/api/reset-password/ResetPasswordService";
import { IDetalheUsuario, userService } from "../../../shared/services/api/user/userService";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { IPermissionInterface } from "../interface/PermissionInterface";
import { ToolbarUserDetail } from "./ToolbarUserDetail";

interface IFormData {
	name: string;
	email: string;
	phone: string;
	password?: string;
	permissionId: string;
	createdFrom: string;
	status: string;
}

const getValidationSchema = (isNew: boolean): yup.SchemaOf<IFormData> =>
	yup.object().shape({
		name: yup.string().required("Informe o nome"),
		email: yup.string().required("Informe o e-mail").email("E-mail inválido"),
		phone: yup.string().required("Informe o telefone"),
		password: isNew ? yup.string().required("Informe a senha") : yup.string().notRequired(),
		permissionId: yup.string().required("Selecione o perfil"),
		createdFrom: yup.string().default("web_app"),
		status: yup.string().default("active").required("Selecione o status"),
	});

export const DetalheUsuariosPresentation: React.FC = () => {
	const { id = "novo" } = useParams<"id">();
	const navigate = useNavigate();
	const { formRef, save, saveAndClose, saveAndNew, issaveAndClose } = useVForm();
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const { permissionLevel, permissionId, isSuperAdmin } = useProfile();
	const isNew = id === "novo";
	const canCreate = useMemo(() => checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Add), []);
	const canUpdate = useMemo(() => checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.Update), []);
	const canResetPassword = useMemo(() => checkByProfileGuard(PermissionRolesEnum.UsersOptions, PermissionActionsEnum.ResetPassword), []);
	const canSave = isNew ? canCreate : canUpdate;
	const isReadOnly = !canSave;

	const [isLoading, setIsLoading] = useState(false);
	const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
	const [isResetSending, setIsResetSending] = useState(false);
	const [nome, setNome] = useState("");
	const [nomeClient, setNomeClient] = useState("");
	const [clientId, setClientId] = useState("");
	const [functionOptions, setFunctionOptions] = useState<IPermissionInterface[]>([]);
	const toastHandler = useToast();
	const [user, setUser] = useState<IDetalheUsuario | null>(null);

	useEffect(() => {
		if (!isNew) {
			setIsLoading(true);

			userService
				.getByID(id)
				.then((result) => {
					setIsLoading(false);
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={"Alerta"} content={result.message} />,
						});
						navigate("/usuarios");
						return;
					}

					setUser(result);
					setNome(result.name);

					const cli = result?.client as Client;
					const nameClient = cli?.name || "";
					const idClient = cli?.id || "";
					setNomeClient(nameClient);
					setClientId(idClient);

					const permissionIdFromApi =
						(result as any).permissionId ?? (result as any).permission_id ?? (result as any).permission?.id ?? "";

					formRef.current?.setData({
						...result,
						clientId: idClient,
						permissionId: permissionIdFromApi,
						status: result.status ?? "active",
						createdFrom: "web_app",
						password: "",
					} as any);
				})
				.catch((err: any) => {
					setIsLoading(false);
					const { titleMessage, contentMessage } = returnMessageError(err);
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		} else {
			setUser(null);
			setNome("");
			setNomeClient("");
			setClientId("");
			formRef.current?.setData({
				status: "active",
				name: "",
				email: "",
				phone: "",
				password: "",
				permissionId: "",
				createdFrom: "web_app",
				clientId: "",
			} as any);
		}
	}, [id, isNew]);

	const permissionOptions = useMemo(() => {
		const currentLevel = permissionLevel;

		return (
			functionOptions
				?.filter((opt) => {
					// Regra: somente usuários level 0 podem atribuir perfis level 0 (super usuário)
					if (opt.level === 0) return currentLevel === 0;

					// Quando ainda não sabemos o level (ex.: contexto carregando), mantém opções (exceto level 0)
					if (currentLevel === null || currentLevel === undefined) return true;

					// Regra geral: não permitir atribuir perfis com mais privilégio (nível menor)
					return opt.level >= currentLevel;
				})
				.map((opt) => ({ id: opt.id, label: opt.description })) ?? []
		);
	}, [functionOptions, permissionLevel]);

	useEffect(() => {
		PermissionServices.getUnlimited(permissionLevel || 99)
			.then((result: any) => {
				const data = result.data as unknown as IPermissionInterface[];
				setFunctionOptions(data);
			})
			.catch((err: any) => {
				const { titleMessage, contentMessage } = returnMessageError(err);

				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			});
	}, []);

	const handleSave = (dados: IFormData) => {
		if (!canSave) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Permissão"} content={"Você não tem permissão para salvar alterações."} />,
			});
			return;
		}

		const schema = getValidationSchema(isNew);

		schema
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados) => {
				setIsLoading(true);
				if (isNew) {
					userService.create(dadosvalidados).then((result: any) => {
						setIsLoading(false);
						if (result instanceof Error) {
							const titleMessage = "Alerta!!";
							const contentMessage = "Erro: " + result?.message;

							toastHandler.present({
								type: "error",
								position: "top-right",
								messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
							});
						} else {
							toastHandler.present({
								type: "success",
								position: "top-right",
								messageOrContent: <ToastContent title={"Sucesso"} content={"Usuário criado com sucesso!"} />,
							});

							if (issaveAndClose()) {
								navigate(`/usuarios/`);
							} else {
								navigate(`/usuarios/detalhe/${result}`);
							}
						}
					});
				} else {
					const selectedPermission = functionOptions.find((p) => p.id === dadosvalidados.permissionId);
					const role = selectedPermission?.clientTypeId ?? "estate_user";

					delete (dadosvalidados as any).password; // não atualiza senha por aqui
					userService.updateById(id, { id: id, ...dadosvalidados, role }).then((result: any) => {
						setIsLoading(false);

						if (result instanceof Error) {
							const titleMessage = "Alerta!!";
							const contentMessage = "Erro: " + result?.message;

							toastHandler.present({
								type: "error",
								position: "top-right",
								messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
							});
						} else {
							toastHandler.present({
								type: "success",
								position: "top-right",
								messageOrContent: <ToastContent title={"Sucesso"} content={"Usuário atualizado com sucesso."} />,
							});

							if (issaveAndClose()) {
								navigate(`/usuarios/`);
							}
						}
					});
				}
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});
	};

	const handleOpenResetDialog = () => {
		if (isNew) return;
		setIsResetDialogOpen(true);
	};

	const handleConfirmResetPassword = async () => {
		if (!canResetPassword) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Permissão"} content={"Você não tem permissão para resetar senha."} />,
			});
			return;
		}

		const emailToReset = user?.email;
		if (!emailToReset) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={"Alerta"} content={"Não foi possível identificar o e-mail do usuário."} />,
			});
			return;
		}

		try {
			setIsResetSending(true);
			await ResetPasswordService.save({ email: emailToReset });
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: (
					<ToastContent
						title={"Email Enviado"}
						content={
							"Foi enviado um link com codigo de recuperação para o email cadastrado, verifique sua caixa de entrada e ou lixo/spam"
						}
					/>
				),
			});
			setIsResetDialogOpen(false);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setIsResetSending(false);
		}
	};

	return (
		<LayoutBaseDePagina
			titulo="Usuários"
			subtitulo={isNew ? "Cadastro de novo usuário" : `Edição de usuário • ${nome}`}
			barraDeFerramentas={
				<ToolbarUserDetail
					isLoading={isLoading}
					isNew={isNew}
					canSave={canSave}
					canCreate={canCreate}
					canResetPassword={canResetPassword}
					onSave={save}
					onSaveAndClose={saveAndClose}
					onResetPassword={handleOpenResetDialog}
					onBack={() => navigate("/usuarios")}
					onNew={() => navigate("/usuarios/detalhe/novo")}
				/>
			}
		>
			<Dialog open={isResetDialogOpen} onClose={() => (isResetSending ? null : setIsResetDialogOpen(false))} maxWidth="xs" fullWidth>
				<DialogTitle>Reenviar reset de senha</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						Deseja reenviar o link de reset de senha para o e-mail do usuário?
					</Typography>
					<Typography variant="subtitle2" sx={{ mt: 2 }}>
						{user?.email ?? "--"}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsResetDialogOpen(false)} disabled={isResetSending}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={handleConfirmResetPassword} disabled={isResetSending}>
						{isResetSending ? "Enviando..." : "Confirmar"}
					</Button>
				</DialogActions>
			</Dialog>
			<VForm
				id="formIdSave"
				placeholder=""
				onPointerEnterCapture={() => undefined}
				onPointerLeaveCapture={() => undefined}
				ref={formRef}
				onSubmit={handleSave}
			>
				<Box
					sx={{
						width: "100%",
						maxWidth: "1300px",
						ml: 1,
						mr: 1,
						pb: 2,
					}}
				>
					<LoadingComp isopen={isLoading} />

					{isReadOnly && (
						<Box mt={1}>
							<Alert severity="info">Você não tem permissão para salvar alterações nesta tela.</Alert>
						</Box>
					)}

					<Stack spacing={2} mt={1}>
						<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" padding={2} borderRadius={2}>
							<Box>
								<Typography variant="h6" color="primary">
									Dados do usuário
								</Typography>
							</Box>

							{isNew && (
								<Box mt={2}>
									<Alert
										severity="info"
										variant="filled"
										sx={{
											backgroundColor: alpha(theme.palette.info.main, 0.12),
											color: theme.palette.text.primary,
											"& .MuiAlert-icon": { color: theme.palette.info.main },
										}}
									>
										<AlertTitle>Informações do cadastro</AlertTitle>
										- O e-mail não poderá ser alterado após o cadastro.
										<br />- A senha é obrigatória para novo usuário.
									</Alert>
								</Box>
							)}

							<Grid container padding={2} spacing={2}>
								<Grid item xs={12} md={6}>
									<AutoCompleteClient
										isExternalLoading={isLoading}
										labelSelect={"Cliente"}
										nameDefault={nomeClient}
										clientDefault={clientId}
										disabled={isReadOnly}
									/>
								</Grid>

								<Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }} />

								<Grid item xs={12} md={4}>
									<VTextField
										fullWidth
										label="Nome"
										name="name"
										disabled={isLoading || isReadOnly}
										onChange={(e) => setNome(e.target.value)}
									/>
								</Grid>

								<Grid item xs={12} md={4}>
									<VMaskTextField fullWidth label="Telefone" name="phone" disabled={isLoading || isReadOnly} mask="cell" />
								</Grid>
                                <Grid item xs={12} md={4}>
									<VTextField fullWidth label="Status" name="status" disabled={isLoading || isReadOnly} select>
										<MenuItem value={"active"}>Ativo</MenuItem>
										<MenuItem value={"inactive"}>Inativo</MenuItem>
										<MenuItem value={"blocked"}>Bloqueado</MenuItem>
									</VTextField>
								</Grid>

								<Grid item xs={12} md={isNew ? 4 : 6}>
									<VTextField
										fullWidth
										label="E-mail"
										name="email"
										disabled={isLoading || !isNew || isReadOnly}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<Email />
												</InputAdornment>
											),
										}}
									/>
								</Grid>

								{isNew && (
									<Grid item xs={12} md={4}>
										<VTextField fullWidth label="Senha" name="password" disabled={isLoading || isReadOnly} type="password" />
									</Grid>
								)}

								<Grid item xs={12} md={isNew ? 4 : 6}>
									<AutoCompletePermission
										isExternalLoading={isLoading}
										labelSelect="Perfil"
										options={permissionOptions}
										disabled={isReadOnly}
									/>
								</Grid>
							</Grid>
						</Box>

						<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" padding={2}>
							<Typography variant="h6" color="primary" gutterBottom>
								Auditoria
							</Typography>

							{!user && (
								<Typography variant="body2" color="text.secondary">
									Após salvar, dados de auditoria aparecerão aqui.
								</Typography>
							)}

							{user && (
								<Stack spacing={1}>
									<Typography variant="subtitle2" fontWeight={"bold"} color={"text.secondary"}>
										Data do cadastro:{" "}
										{user?.createdAt || user?.created_at ? moment(user.createdAt || user.created_at).format("DD/MM/YYYY HH:mm") : "--"}
									</Typography>
									<Typography variant="subtitle2" fontWeight={"bold"} color={"text.secondary"}>
										Último login: {!user?.last_login ? "--" : moment(user.last_login).format("DD/MM/YYYY HH:mm")}
									</Typography>
								</Stack>
							)}
						</Box>
					</Stack>
				</Box>
			</VForm>
		</LayoutBaseDePagina>
	);
};
